/**
 * ValiderLotModal — VALIDATE (freeze stats) a brouillon QC lot (POST /qc/lots/:id/valider, cap
 * qc.lot.manage). Validation FREEZES cible + ecart_type (a re-cal is a new lot, never an in-place
 * edit — médico-légal). ecart_type must be strictly > 0 (server-enforced, mirrored client-side).
 * Pre-fills from the lot's draft stats. Gated by useCan.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { QC_CAPS } from "../caps";
import { useLabId, useValiderLot } from "../api";
import type { LotControle } from "../types";
import { idStr } from "../types";
import { parseNum } from "../lib";

export interface ValiderLotModalProps {
  open: boolean;
  lot: LotControle | null;
  onClose: () => void;
  onValidated: () => void;
}

export function ValiderLotModal({ open, lot, onClose, onValidated }: ValiderLotModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canManage = useCan(QC_CAPS.lotManage);
  const mutation = useValiderLot(labId);

  const [cible, setCible] = useState("");
  const [sd, setSd] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCible(lot?.cible != null ? String(lot.cible) : "");
    setSd(lot?.ecart_type != null ? String(lot.ecart_type) : "");
    setTouched(false);
  }, [open, lot]);

  const cibleNum = parseNum(cible);
  const sdNum = parseNum(sd);
  const cibleError = touched && cibleNum == null ? t("qc.field.numInvalid") : null;
  const sdError = touched && (sdNum == null ? t("qc.field.numInvalid") : sdNum <= 0 ? t("qc.field.sdInvalid") : null);

  const valid = cibleNum != null && sdNum != null && sdNum > 0;

  const handleClose = () => {
    if (mutation.isPending) return;
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canManage || !lot) return;
    mutation.mutate(
      { lotId: idStr(lot.id), cible: cibleNum, ecart_type: sdNum },
      {
        onSuccess: () => {
          toast.success(t("qc.lots.valider.success"));
          onValidated();
        },
        onError: (err) => toast.error(err instanceof ApiError ? err.message : t("qc.field.required")),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={`${t("qc.lots.valider.title")}${lot ? ` — ${lot.code}` : ""}`}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("qc.action.cancel")}
          </Button>
          <Button
            type="submit"
            form="qc-valider-lot"
            loading={mutation.isPending}
            disabled={!canManage || !valid}
          >
            {t("qc.lots.valider.submit")}
          </Button>
        </>
      }
    >
      <form id="qc-valider-lot" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <p
            style={{
              margin: 0,
              padding: "var(--nv-s-3)",
              borderRadius: "var(--nv-r-sm)",
              background: "var(--nv-tint-high)",
              color: "var(--nv-high)",
              fontSize: "var(--nv-fs-caption)",
            }}
            role="note"
          >
            {t("qc.lots.valider.warn")}
          </p>

          <FormField label={t("qc.lots.field.cible")} required {...(cibleError ? { error: cibleError } : {})}>
            {(p) => (
              <Input
                {...p}
                autoFocus
                mono
                inputMode="decimal"
                value={cible}
                invalid={!!cibleError}
                onChange={(e) => setCible(e.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("qc.lots.field.sd")} hint={t("qc.lots.field.sd.hint")} required {...(sdError ? { error: sdError } : {})}>
            {(p) => (
              <Input
                {...p}
                mono
                inputMode="decimal"
                value={sd}
                invalid={!!sdError}
                onChange={(e) => setSd(e.target.value)}
              />
            )}
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
